import clsx from 'clsx';
import React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';
import Icon from '../../atoms/Icon/Icon';
import useDraggableItemsList from './useDraggableItemsList';

/**
 * DraggableItemsList — v2 molecule.
 *
 * Wrapper genérico de drag & drop (sobre `react-beautiful-dnd`) sin lógica de
 * dominio: el consumidor inyecta el contenido de cada item vía `item.content`.
 * Renderiza una lista semántica `<ul>/<li>` y expone un asa de arrastre
 * accesible (icono `drag-bullets` con `aria-label` traducido).
 */

export type DraggableItem = {
  content: React.ReactNode;
  guid: string;
  [key: string]: unknown;
};

export type DraggableItemsListProps = {
  className?: string;
  disableDrag?: boolean;
  disableEditMode?: boolean;
  handleOnDragEnd?: (result: DropResult) => void;
  items?: DraggableItem[];
  testId?: string;
};

const DraggableItemsList = React.forwardRef<HTMLDivElement, DraggableItemsListProps>(
  ({ className, disableDrag = false, disableEditMode = true, handleOnDragEnd = () => {}, items = [], testId }, ref) => {
    const { dragHandleLabel, getListStyle, isDragDisabled, onDragEnd } = useDraggableItemsList({
      disableDrag,
      disableEditMode,
      handleOnDragEnd,
    });

    return (
      <div ref={ref} className={clsx('ui-draggable-items-list', className)} data-testid={testId}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable-general" type="droppableItem">
            {(droppableProvided, droppableSnapshot) => (
              <ul
                className="ui-draggable-items-list__list"
                ref={droppableProvided.innerRef}
                style={getListStyle(droppableSnapshot.isDraggingOver)}
                {...droppableProvided.droppableProps}
              >
                {items.map((item, index) => (
                  <Draggable draggableId={item.guid} index={index} isDragDisabled={isDragDisabled} key={item.guid}>
                    {(draggableProvided, draggableSnapshot) => (
                      <li
                        className="ui-draggable-items-list__item"
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        {...draggableProvided.dragHandleProps}
                      >
                        {!isDragDisabled && !draggableSnapshot.isDragging && (
                          <span className="ui-draggable-items-list__handle">
                            <Icon name="drag-bullets" size="md" title={dragHandleLabel} />
                          </span>
                        )}
                        <div className="ui-draggable-items-list__content">{item.content}</div>
                      </li>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  },
);

DraggableItemsList.displayName = 'DraggableItemsList';

export default DraggableItemsList;
